import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useFormInputHandler } from './useFormInputHandler';
import { useFormValidation } from './useFormValidation';
import { useModalSubmit } from './useModalSubmit';

/**
 * Modal CRUD state yönetimi hook'u.
 *
 * Mevcut API korunarak genişletilmiştir:
 * - validationSchema   → verilirse useFormValidation entegre edilir, validate() döner
 * - normalizeInput     → true ise useFormInputHandler entegre edilir, handleInputChange döner
 * - submitConfig       → verilirse useModalSubmit entegre edilir, handleSubmit + isSubmitting döner
 * - customHandlers     → useFormInputHandler'a geçirilir (field-spesifik override)
 *
 * Opsiyonel parametreler verilmezse davranış tamamen eskisiyle aynıdır (geriye uyumlu).
 */
export const useModalCrudState = ({
    isOpen,
    mode,
    selectedItem,
    initialData,
    toggle,
    mapSelectedToData,
    // --- Yeni opsiyonel parametreler ---
    validationSchema,
    normalizeInput = false,
    customHandlers,
    submitConfig,
}) => {
    const [formData, setFormData] = useState(initialData);
    const [errors, setErrors] = useState({});

    // initialData ve mapSelectedToData inline tanımlandığı için her renderda yeni referans alır.
    // Ref ile stabilize ederek useEffect'in gereksiz yere tetiklenmesini önleriz.
    const initialDataRef = useRef(initialData);
    initialDataRef.current = initialData;

    const mapSelectedToDataRef = useRef(mapSelectedToData);
    mapSelectedToDataRef.current = mapSelectedToData;

    const resetForm = useCallback(() => {
        setFormData(initialDataRef.current);
        setErrors({});
    }, []);

    const handleCancel = useCallback(() => {
        resetForm();
        toggle();
    }, [resetForm, toggle]);

    useEffect(() => {
        if (!isOpen) return;

        if (mode === 'modify' && selectedItem) {
            const mapper = mapSelectedToDataRef.current;
            const mappedData = mapper
                ? mapper({ initialData: initialDataRef.current, selectedItem })
                : { ...initialDataRef.current, ...selectedItem };

            setFormData(mappedData);
            setErrors({});
            return;
        }

        resetForm();
    }, [isOpen, mode, selectedItem, resetForm]);

    // --- Opsiyonel: normalizeInput aktifse handleInputChange döner ---
    const handleInputChange = useFormInputHandler(
        normalizeInput ? setFormData : () => {},
        normalizeInput ? setErrors : () => {},
        normalizeInput ? { customHandlers } : {},
    );

    // --- Opsiyonel: validationSchema verilmişse validate() döner ---
    const stableSchema = useMemo(() => validationSchema || {}, [validationSchema]);
    const { validate } = useFormValidation(stableSchema, formData, setErrors);

    // --- Opsiyonel: submitConfig verilmişse handleSubmit + isSubmitting döner ---
    const { handleSubmit, isSubmitting } = useModalSubmit({
        validate: submitConfig ? validate : () => ({}),
        buildPayload: submitConfig?.buildPayload,
        apiEndpoint: submitConfig?.apiEndpoint || '',
        mode: submitConfig?.mode || mode,
        selectedItem: submitConfig?.selectedItem ?? (mode === 'modify' ? selectedItem : null),
        refreshData: submitConfig?.refreshData || (() => {}),
        handleCancel,
        componentName: submitConfig?.componentName || '',
        formData,
        httpMethod: submitConfig?.httpMethod,
        isFormData: submitConfig?.isFormData,
        onSuccess: submitConfig?.onSuccess,
    });

    return {
        // --- Mevcut API (geriye uyumlu) ---
        formData,
        setFormData,
        errors,
        setErrors,
        resetForm,
        handleCancel,
        // --- Yeni API ---
        ...(normalizeInput && { handleInputChange }),
        ...(validationSchema && { validate }),
        ...(submitConfig && { handleSubmit, isSubmitting }),
    };
};
